import { useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { getDepartmentList } from 'services/api/dashboard';
import { Button, message, Tabs } from 'antd';
import { useTranslate } from '@tolgee/react';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { getStoreCount } from 'components/StoreScopes';
import Empty from 'components/Empty';
import Tiktok from './Tiktok';
import Shopee from './Shopee';
import styled from '@emotion/styled';
import Lazada from './Lazada';
import useModal from 'hooks/useModal';
import ExportModal from './ExportModal';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import DataUpdate from '../DataUpdate';

function PlatformData() {
  const { t } = useTranslate();

  const exportable = useGetPermission()('导出平台数据');

  const [platform, setPlatform] = useState<string>();

  const { loading, data } = useRequest(
    () =>
      getDepartmentList('store_performance').then((res) => {
        const platform = (['SHOPEE', 'LAZADA', 'TIKTOK', 'TIKTOK_US'] as const).find((platform) => {
          return res.departments.some(({ platformStoreScopes }) =>
            getStoreCount(platformStoreScopes.filter((_) => _.platform === platform)),
          );
        });
        if (platform) {
          setPlatform(platform);
        }
        return res.departments;
      }),
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { visible, show, hide } = useModal();

  const {
    shopeeDepartments,
    lazadaDepartments,
    tiktokDepartments,
    tiktokUsDepartments,
    platforms,
  } = useMemo(() => {
    const shopeeDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'SHOPEE')),
      ) || [];
    const lazadaDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'LAZADA')),
      ) || [];
    const tiktokDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'TIKTOK')),
      ) || [];
    const tiktokUsDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'TIKTOK_US')),
      ) || [];
    const platforms: General.PlatformUpperCase[] = [];
    if (shopeeDepartments.length) {
      platforms.push('SHOPEE');
    }
    if (lazadaDepartments.length) {
      platforms.push('LAZADA');
    }
    if (tiktokDepartments.length) {
      platforms.push('TIKTOK');
    }
    if (tiktokUsDepartments.length) {
      platforms.push('TIKTOK_US');
    }
    return {
      shopeeDepartments,
      lazadaDepartments,
      tiktokDepartments,
      tiktokUsDepartments,
      platforms,
    };
  }, [data]);

  const [updateTime, setUpdateTime] = useState<string>();

  if (!data) {
    return loading ? <LoadingOutlined /> : <></>;
  }

  if (!platform) {
    return <Empty />;
  }

  return (
    <Container>
      <Tabs
        activeKey={platform}
        onChange={setPlatform}
        tabBarExtraContent={
          <div className="flex items-center">
            {updateTime && (
              <DataUpdate content={t('ViewDashboard.nav.每日更新昨日数据', { time: updateTime })} />
            )}
            {exportable && <Button className="ml-4" icon={<DownloadOutlined />} onClick={show} />}
          </div>
        }
      >
        {shopeeDepartments.length > 0 && <Tabs.TabPane key="SHOPEE" tab="Shopee" />}
        {lazadaDepartments.length > 0 && <Tabs.TabPane key="LAZADA" tab="Lazada" />}
        {tiktokDepartments.length > 0 && <Tabs.TabPane key="TIKTOK" tab="Tiktok" />}
        {tiktokUsDepartments.length > 0 && <Tabs.TabPane key="TIKTOK_US" tab="Tiktok US" />}
      </Tabs>
      <ExportModal visible={visible} onCancel={hide} departments={data} platforms={platforms} />
      {shopeeDepartments.length > 0 && (
        <Shopee
          visible={platform === 'SHOPEE'}
          departments={shopeeDepartments}
          onUpdateTimeChange={setUpdateTime}
        />
      )}
      {lazadaDepartments.length > 0 && (
        <Lazada
          visible={platform === 'LAZADA'}
          departments={lazadaDepartments}
          onUpdateTimeChange={setUpdateTime}
        />
      )}
      {tiktokDepartments.length > 0 && (
        <Tiktok
          key="TIKTOK"
          platform="TIKTOK"
          visible={platform === 'TIKTOK'}
          departments={tiktokDepartments}
          onUpdateTimeChange={setUpdateTime}
        />
      )}
      {tiktokUsDepartments.length > 0 && (
        <Tiktok
          key="TIKTOK_US"
          platform="TIKTOK_US"
          visible={platform === 'TIKTOK_US'}
          departments={tiktokUsDepartments}
          onUpdateTimeChange={setUpdateTime}
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  > .ant-tabs {
    position: sticky;
    top: 0;
    margin-top: 16px;
    background: #fff;
    z-index: 2;
    .ant-tabs-nav {
      padding: 0 16px;
      margin-bottom: 0;
    }
  }
  > header {
    position: sticky;
    top: 46px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 16px;
    background: #fff;
    z-index: 2;
    > section {
      display: flex;
      align-items: center;
      > * {
        margin-right: 16px;
      }
    }
  }
  > section {
    margin-top: 16px;
    > header {
      display: flex;
      align-items: center;
      height: 56px;
      padding: 0 24px;
      margin-bottom: 1px;
      background: #fff;
      > h3 {
        margin-bottom: 0;
        font-size: 16px;
        font-weight: 500;
      }
      > .anticon {
        margin-left: 6px;
        cursor: help;
      }
    }
    > div {
      padding: 24px;
      background: #fff;
      > section {
        height: 320px;
      }
      > ul {
        display: flex;
        align-items: center;
        margin: 0;
        padding: 0;
        list-style: none;
        margin-bottom: 16px;
        > dl {
          flex: 0 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 280px;
          height: 44px;
          margin-bottom: 0;
          margin-right: 20px;
          padding: 0 12px;
          background: #f7f9fa;
          dt {
            display: flex;
            align-items: center;
            font-weight: 400;
            color: #515359;
            .anticon {
              margin-left: 4px;
              cursor: help;
            }
          }
          dd {
            margin-bottom: 0;
            font-weight: 500;
            color: #232426;
          }
        }
      }
    }
  }
`;

export default PlatformData;
